import { useState } from "react";
import { useFetch } from "@/lib/hooks";
import { api } from "@/lib/api";
import { ItemCard } from "@/components/ItemCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORY_LABELS, type ItemCategory } from "@/lib/types";
import { Search, SlidersHorizontal, X } from "lucide-react";

export function BrowsePage() {
	const { data: items, loading } = useFetch(() => api.items.list(), []);
	const [search, setSearch] = useState("");
	const [category, setCategory] = useState<string>("all");
	const [sortBy, setSortBy] = useState<string>("newest");
	const [showFilters, setShowFilters] = useState(false);

	const filtered = items
		?.filter((item) => {
			const matchesSearch =
				!search ||
				item.title.toLowerCase().includes(search.toLowerCase()) ||
				item.description.toLowerCase().includes(search.toLowerCase()) ||
				item.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
			const matchesCategory = category === "all" || item.category === category;
			return matchesSearch && matchesCategory;
		})
		.sort((a, b) => {
			if (sortBy === "newest") return b.createdAt.localeCompare(a.createdAt);
			if (sortBy === "value-high") return b.estimatedValue - a.estimatedValue;
			if (sortBy === "value-low") return a.estimatedValue - b.estimatedValue;
			return 0;
		});

	const activeFilters = (category !== "all" ? 1 : 0) + (search ? 1 : 0);

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Browse Items</h1>
				<p className="text-muted-foreground mt-1">
					Discover items available for trade. Find something you want and propose a swap.
				</p>
			</div>

			<div className="flex flex-col sm:flex-row gap-3">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder="Search items, tags, descriptions..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9"
					/>
					{search && (
						<button
							onClick={() => setSearch("")}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
						>
							<X className="h-4 w-4" />
						</button>
					)}
				</div>
				<div className="flex gap-2">
					<Button
						variant={showFilters ? "secondary" : "outline"}
						size="default"
						onClick={() => setShowFilters(!showFilters)}
						className="relative"
					>
						<SlidersHorizontal className="h-4 w-4 mr-1.5" />
						Filters
						{activeFilters > 0 && (
							<Badge className="ml-1.5 h-5 w-5 p-0 flex items-center justify-center text-[10px]">
								{activeFilters}
							</Badge>
						)}
					</Button>
					<Select value={sortBy} onValueChange={setSortBy}>
						<SelectTrigger className="w-[150px]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="newest">Newest</SelectItem>
							<SelectItem value="value-high">Value: High</SelectItem>
							<SelectItem value="value-low">Value: Low</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>

			{showFilters && (
				<div className="flex flex-wrap gap-2 p-4 rounded-lg border bg-card">
					<Badge
						variant={category === "all" ? "default" : "outline"}
						className="cursor-pointer"
						onClick={() => setCategory("all")}
					>
						All Categories
					</Badge>
					{(Object.entries(CATEGORY_LABELS) as [ItemCategory, string][]).map(([key, label]) => (
						<Badge
							key={key}
							variant={category === key ? "default" : "outline"}
							className="cursor-pointer"
							onClick={() => setCategory(key)}
						>
							{label}
						</Badge>
					))}
				</div>
			)}

			{loading ? (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
					{Array.from({ length: 8 }).map((_, i) => (
						<div key={i} className="rounded-lg border bg-card animate-pulse">
							<div className="aspect-[4/3] bg-muted" />
							<div className="p-4 space-y-2">
								<div className="h-4 bg-muted rounded w-3/4" />
								<div className="h-3 bg-muted rounded w-full" />
								<div className="h-3 bg-muted rounded w-1/2" />
							</div>
						</div>
					))}
				</div>
			) : (
				<>
					<p className="text-sm text-muted-foreground">
						{filtered?.length || 0} items available
					</p>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
						{filtered?.map((item) => (
							<ItemCard key={item.id} item={item} />
						))}
					</div>
					{filtered?.length === 0 && (
						<div className="text-center py-12">
							<p className="text-muted-foreground">No items match your search.</p>
							<Button variant="link" onClick={() => { setSearch(""); setCategory("all"); }}>
								Clear filters
							</Button>
						</div>
					)}
				</>
			)}
		</div>
	);
}
